import React, { useState, useEffect, useCallback, useContext } from 'react'; // useCallback eklendi
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config/constants';
import Modal from './Modal';
// Space, Tabs eklendi
import { Select, Input, Button as AntButton, notification, Spin, Tooltip, Divider, Space, Alert, Tabs } from 'antd'; // Alert, Tabs eklendi
import { LoadingOutlined, UploadOutlined, DeleteOutlined, InfoCircleOutlined, FilePdfOutlined, EyeOutlined, FileExcelOutlined, FileWordOutlined, FileZipOutlined, FileOutlined } from '@ant-design/icons'; // Ant Design ikonları eklendi
import type { SelectProps } from 'antd';
import ColorSelector, { Color as SelectorColorType } from './ColorSelector'; // SelectorColorType olarak import edildi
// Import spec form state types if they are exported from TechnicalSpecSheet
import TechnicalSpecSheet from './TechnicalSpecSheet';
// Import Fabric type from its definition source
import FabricSelector, { Fabric } from './FabricSelector'; // Adjust path if needed
// Import Accessory type from its definition source
import AccessorySelector from './AccessorySelector';
import type { TechnicalSpec } from './TechnicalSpecSheet';
import { Card, SectionTitle } from './SharedComponents'; // Assuming SharedComponents exist
import { AuthContext } from '../context/AuthContext';

// Local Color interface (keep if not imported)
// SelectorColorType kullanıldığı için bu arayüz kaldırılabilir veya SelectorColorType ile eşlenik tutulabilir.
/*
interface Color {
  _id: string;
  name: string;
  firmCode: string;
  tcxCode: string;
  hexCode?: string; // hexCode eklendi
}
*/

// Local Accessory type for internal state management (quantity as number)
interface LocalAccessory extends Omit<Accessory, 'quantity' | '_id'> { // Omit string quantity and potentially _id if it's optional
    _id?: string; // Make _id optional for newly added accessories before saving
    quantity: number; // Keep quantity as number
}

// Product interface (ensure it matches backend expectations)
interface Product {
  _id: string;
  modelName: string;
  purchasingGroup: 'knitting' | 'weaving' | 'triko' | 'denim';
  modelStyle?: string;
  modelImage?: string;
  imageUrl?: string;
  imageUrls?: string[];
  fabric: string;
  color: string;
  description: string;
  isPrototype: boolean;
  prototypeCode?: string;
  productCode?: string;
  status: string;
  accessories: Accessory[];
  technicalSpec?: TechnicalSpec;
  updatedAt?: string; // updatedAt alanı eklendi
  createdAt?: string;
}

// Use API_URL directly for assets
// const BACKEND_ASSET_URL = API_URL;

// --- Helper Components ---
// File Upload Area Component for Editing
interface FileUploadAreaEditProps {
  title: string;
  existingUrls: string[]; // URLs from the server
  newFiles: File[]; // Newly selected files
  newPreviewUrls: string[]; // Previews for new files
  onFilesSelected: (files: File[]) => void;
  onRemoveExisting: (index: number) => void;
  onRemoveNew: (index: number) => void;
  maxFiles: number;
  accept: string;
  fileTypeLabel: string;
}

const FileUploadAreaEdit: React.FC<FileUploadAreaEditProps> = ({
  title, existingUrls, newFiles, newPreviewUrls, onFilesSelected, onRemoveExisting, onRemoveNew, maxFiles, accept, fileTypeLabel
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFilesSelected(Array.from(e.target.files));
    }
    e.target.value = ''; // Reset input
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; if (!isDragging) setIsDragging(true); };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    onFilesSelected(Array.from(e.dataTransfer.files));
  };

  const currentTotalCount = existingUrls.length + newFiles.length;
  const canUploadMore = currentTotalCount < maxFiles;

  return (
    <Card>
      <SectionTitle>{title}</SectionTitle>
      <div className="pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {fileTypeLabel.charAt(0).toUpperCase() + fileTypeLabel.slice(1)} Yükle (Mevcut: {existingUrls.length}, Yeni: {newFiles.length}, Maks: {maxFiles})
        </label>
        {/* Dropzone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ease-in-out mb-6
            ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}
            ${!canUploadMore ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          onClick={() => canUploadMore && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            onChange={handleFileChange}
            className="hidden"
            disabled={!canUploadMore}
          />
          <div className="flex flex-col items-center">
            <UploadOutlined className={`text-4xl mb-3 ${isDragging ? 'text-indigo-600' : 'text-gray-400'}`} />
            <p className={`text-sm font-medium ${isDragging ? 'text-indigo-700' : 'text-gray-600'}`}>
              {fileTypeLabel} dosyalarını buraya sürükleyin veya seçin
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Desteklenen tipler: {accept.split(',').map(t => t.split('/')[1] || t).join(', ').toUpperCase()} (Maks 5MB)
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Toplam Yüklenen: {currentTotalCount} / {maxFiles}
            </p>
          </div>
          {!canUploadMore && (
            <div className="absolute inset-0 bg-gray-200 bg-opacity-75 flex items-center justify-center rounded-lg">
              <p className="text-sm font-semibold text-gray-700">Maksimum dosya limitine ulaşıldı.</p>
            </div>
          )}
        </div>

        {/* Previews */}
        {(existingUrls.length > 0 || newFiles.length > 0) && (
          <div className="mt-6">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Yüklenen Dosyalar:</h5>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {/* Existing Files */}
              {existingUrls.map((url: string, index: number) => {
                const isPdf = url.toLowerCase().endsWith('.pdf');
                return (
                  <div key={`existing-${fileTypeLabel}-${index}`} className="relative group aspect-w-1 aspect-h-1">
                    {isPdf ? (
                      <a href={API_URL+url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center h-full w-full border rounded-md bg-gray-100 p-2 text-blue-600 hover:text-blue-800">
                        <FilePdfOutlined style={{ fontSize: '24px' }} className="text-red-500 mb-1" />
                        <span className="text-xs break-all">{url.split('/').pop()}</span>
                      </a>
                    ) : (
                      <img
                        src={API_URL+url}
                        alt={`Mevcut ${fileTypeLabel} ${index + 1}`}
                        className="object-cover w-full h-full rounded-md border border-gray-200 shadow-sm"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => onRemoveExisting(index)}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      title="Bu dosyayı kaldır"
                    >
                      <DeleteOutlined style={{ fontSize: '12px' }} />
                    </button>
                  </div>
                );
              })}
              {/* New Files */}
              {newPreviewUrls.map((url, index) => (
                <div key={`new-${fileTypeLabel}-${index}`} className="relative group aspect-w-1 aspect-h-1">
                  <img
                    src={url}
                    alt={`Yeni ${fileTypeLabel} ${index + 1}`}
                    className={`object-cover w-full h-full rounded-md border border-blue-300 shadow-sm ${url === '/pdf-icon.png' ? 'object-contain p-2' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveNew(index)}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                    title="Bu dosyayı kaldır"
                  >
                    <DeleteOutlined style={{ fontSize: '12px' }} />
                  </button>
                </div>
              ))}
            </div>
            {/* Clear All Button */}
            {(existingUrls.length > 0 || newFiles.length > 0) && (
              <div className="mt-4 flex justify-end">
                <AntButton size="small" onClick={() => {
                  existingUrls.forEach((_, i) => onRemoveExisting(i));
                  newFiles.forEach((_, i) => onRemoveNew(i));
                }}>
                  Tümünü Temizle
                </AntButton>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// --- Main ProductEdit Component ---
const ProductEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sizeTable, setSizeTable] = useState<{
    columns: string[];
    rows: string[];
    data: Record<string, Record<string, string>>;
  } | null>(null);
  // formData stores fields directly editable in the form (excluding complex objects like accessories, techSpec)
  const [formData, setFormData] = useState<Partial<Omit<Product, 'accessories' | 'technicalSpec' | 'color' | 'fabric'>> & { color?: string; fabric?: string }>({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // For main image upload
  // Use LocalAccessory for state to manage quantity as number
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  // Removed newAccessory and editingAccessory states, assuming AccessorySelector handles this
  // colors state kaldırıldı, ColorSelector kendi renklerini yönetecek
  // const [colors, setColors] = useState<Color[]>([]); // List of available colors
  const [selectedColor, setSelectedColor] = useState<SelectorColorType | null>(null); // For ColorSelector display
  const [selectedTab, setSelectedTab] = useState<'details' | 'technicalSpecs'>('details');
  const [selectedFabric, setSelectedFabric] = useState<Fabric | null>(null); // For FabricSelector display
  const { hasPermission, isAdmin } = useContext(AuthContext);
  // Technical Spec State
  const [technicalSpec, setTechnicalSpec] = useState<TechnicalSpec>({
    dynamicSizeTable: {
      columns: [],
      rows: [],
      data: {}
    },
    sizeSpecs: [],
    measurementFields: [],
    sizeSpecFields: [],
    fabricSpecs: [],
    stitchDetails: [],
    printingDetails: [],
    careInstructions: [],
    qualityStandards: [],
    notes: ''
  });

  // State for dynamic size fields in TechnicalSpecSheet
  const [customSizeFields, setCustomSizeFields] = useState<{ key: string; label: string }[]>([]);
  // State for confirming custom field deletion
  const [deleteFieldConfirmation, setDeleteFieldConfirmation] = useState<string | null>(null);

  // State for image uploads
  const [technicalDrawingFiles, setTechnicalDrawingFiles] = useState<File[]>([]);
  const [technicalDrawingPreviewUrls, setTechnicalDrawingPreviewUrls] = useState<string[]>([]);
  const [existingTechnicalDrawingUrls, setExistingTechnicalDrawingUrls] = useState<string[]>([]);
  const [measurementGuideFiles, setMeasurementGuideFiles] = useState<File[]>([]);
  const [measurementGuidePreviewUrls, setMeasurementGuidePreviewUrls] = useState<string[]>([]);
  const [existingMeasurementGuideUrls, setExistingMeasurementGuideUrls] = useState<string[]>([]);

  // --- Image State ---
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]); // Mevcut görsellerin URL'leri
  const [newlySelectedFiles, setNewlySelectedFiles] = useState<File[]>([]); // Yeni seçilen dosyalar
  const [newImagePreviewUrls, setNewImagePreviewUrls] = useState<string[]>([]); // Yeni dosyaların önizlemeleri
  const [isDragging, setIsDragging] = useState(false); // Sürükleme durumu

  // --- Technical Drawing State ---
  const [newTechnicalDrawingFiles, setNewTechnicalDrawingFiles] = useState<File[]>([]); // Renamed from technicalDrawingFiles
  const [newTechnicalDrawingPreviewUrls, setNewTechnicalDrawingPreviewUrls] = useState<string[]>([]); // Renamed from technicalDrawingPreviewUrls
  const [isTechDrawingDragging, setIsTechDrawingDragging] = useState(false);

  // --- Measurement Guide State ---
  const [newMeasurementGuideFiles, setNewMeasurementGuideFiles] = useState<File[]>([]); // Renamed from measurementGuideFiles
  const [newMeasurementGuidePreviewUrls, setNewMeasurementGuidePreviewUrls] = useState<string[]>([]); // Renamed from measurementGuidePreviewUrls
  const [isMeasurementGuideDragging, setIsMeasurementGuideDragging] = useState(false);

  // Model dokümanları için state'ler
  const [modelDocumentFiles, setModelDocumentFiles] = useState<File[]>([]);
  const [modelDocumentPreviewUrls, setModelDocumentPreviewUrls] = useState<string[]>([]);
  const [existingModelDocumentUrls, setExistingModelDocumentUrls] = useState<string[]>([]);

  // Model doküman state'leri
  const [newModelDocumentFiles, setNewModelDocumentFiles] = useState<File[]>([]);
  const [newModelDocumentPreviewUrls, setNewModelDocumentPreviewUrls] = useState<string[]>([]);
  const [isModelDocDragging, setIsModelDocDragging] = useState(false);

  // Model dokümanları için state
  const [modelDocuments, setModelDocuments] = useState<ModelDocument[]>([]);

  // State için yeni değişken ekle
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  // --- useEffect Hooks ---

  // Fetch initial product data and color list
  useEffect(() => {
    if (id) {
      fetchProduct();
    } else {
      setError('Ürün ID bulunamadı.');
      setLoading(false);
    }
  }, [id]);

  // Fetch color details when formData.color changes
  useEffect(() => {
    const colorId = formData.color;
    if (colorId && typeof colorId === 'string' && colorId.trim() !== '') {
      if (!selectedColor || selectedColor._id !== colorId) {
        fetch(`${API_URL}/colors/${colorId}`)
          .then(response => {
            if (!response.ok) {
              return response.text().then(text => Promise.reject(new Error(`Renk API hatası (ID: ${colorId}, Durum: ${response.status}): ${text}`)));
            }
            return response.json();
          })
          .then((data: SelectorColorType) => {
            if (data && data._id) {
              setSelectedColor(data);
            } else {
              console.warn('[ProductEdit useEffect] API\'den geçersiz renk verisi alındı:', data);
              setSelectedColor(null);
              notification.error({ message: 'Renk Yükleme Hatası', description: `API'den gelen renk verisi geçersiz (ID: ${colorId}).` });
            }
          })
          .catch(error => {
            console.error(`[ProductEdit useEffect] ❌ Renk bilgisi yüklenirken hata (ID: ${colorId}):`, error);
            setSelectedColor(null);
            notification.error({ message: 'Renk Yükleme Hatası', description: `Seçilen renk detayları (ID: ${colorId}) yüklenemedi: ${error.message}` });
          });
      }
    } else if (!colorId && selectedColor) {
      setSelectedColor(null);
    }
  }, [formData.color, selectedColor, API_URL]);

  // Fetch fabric details when formData.fabric (ID) changes
  useEffect(() => {
    const fabricId = formData.fabric;
    if (fabricId && typeof fabricId === 'string' && fabricId.trim() !== '') {
      if (!selectedFabric || selectedFabric._id !== fabricId) {
        fetch(`${API_URL}/fabrics/${fabricId}`)
          .then(response => {
            if (!response.ok) {
              return response.text().then(text => Promise.reject(new Error(`Kumaş API hatası (ID: ${fabricId}, Durum: ${response.status}): ${text}`)));
            }
            return response.json();
          })
          .then((data: Fabric) => {
            if (data && data._id) {
              setSelectedFabric(data);
            } else {
              setSelectedFabric(null);
              notification.error({ message: 'Kumaş Yükleme Hatası', description: `API'den gelen kumaş verisi geçersiz (ID: ${fabricId}).` });
            }
          })
          .catch(error => {
            console.error(`[ProductEdit useEffect] ❌ Kumaş bilgisi yüklenirken hata (ID: ${fabricId}):`, error);
            setSelectedFabric(null);
            notification.error({ message: 'Kumaş Yükleme Hatası', description: `Seçilen kumaş detayları (ID: ${fabricId}) yüklenemedi: ${error.message}` });
          });
      }
    } else if (!fabricId && selectedFabric) {
      setSelectedFabric(null);
    }
  }, [formData.fabric, selectedFabric, API_URL]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      newImagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
      technicalDrawingPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      measurementGuidePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [newImagePreviewUrls, technicalDrawingPreviewUrls, measurementGuidePreviewUrls]);

  // --- Data Fetching Functions ---

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/products/${id}`);
      if (!response.ok) {
        setError('Ürün getirilemedi.');
        setLoading(false);
        return;
      }
      const productData = await response.json();
      setProduct(productData);
      setFormData({
        modelName: productData.modelName || '',
        purchasingGroup: productData.purchasingGroup || 'knitting',
        modelStyle: productData.modelStyle || '',
        fabric: typeof productData.fabric === 'object' ? productData.fabric._id : productData.fabric || '',
        color: typeof productData.color === 'object' ? productData.color._id : productData.color || '',
        description: productData.description || '',
        isPrototype: productData.isPrototype || false,
        prototypeCode: productData.prototypeCode || '',
        productCode: productData.productCode || '',
        status: productData.status || 'active'
      });
      // Aksesuarları doğrudan state'e aktar
      setAccessories(productData.accessories || []);
      // Kumaş obje olarak geldiyse doğrudan seçili kumaş state'ine ata
      if (productData.fabric && typeof productData.fabric === 'object') {
        setSelectedFabric(productData.fabric);
      }
      // Teknik föy ve beden tablosunu doğrudan state'e aktar
      if (productData.technicalSpec) {
        setTechnicalSpec(productData.technicalSpec);
        setExistingTechnicalDrawingUrls(productData.technicalSpec.technicalDrawingUrls || []);
        setExistingMeasurementGuideUrls(productData.technicalSpec.measurementGuideUrls || []);
      }
      setLoading(false);
    } catch (error) {
      setError('Ürün getirilemedi.');
      setLoading(false);
    }
  };

  // Model dokümanlarını fetch et
  const fetchModelDocuments = async () => {
    try {
      console.log('[DEBUG] Model dokümanları fetch ediliyor...');
      const response = await fetch(`${API_URL}/products/${id}`);
      console.log('[DEBUG] API yanıtı:', response.status);
      
      if (!response.ok) {
        throw new Error('Ürün bilgileri alınamadı');
      }
      
      const productData = await response.json();
      console.log('[DEBUG] Alınan ürün verisi:', productData);
      
      if (productData?.technicalSpec?.modelDocumentUrls) {
        // Her bir doküman için detay bilgilerini al
        const documentPromises = productData.technicalSpec.modelDocumentUrls.map(async (url: string) => {
          try {
            // HEAD isteği ile dosya boyutunu al
            const fileResponse = await fetch(`${API_URL}${url}`, { method: 'HEAD' });
            const size = fileResponse.headers.get('content-length');
            
            return {
              id: url,
              originalName: url.split('/').pop() || url,
              displayName: url.split('/').pop() || url,
              uploadDate: productData.updatedAt || new Date(),
              url: url,
              type: url.split('.').pop()?.toLowerCase() || 'unknown',
              size: size ? parseInt(size, 10) : 0
            };
          } catch (error) {
            console.error(`[DEBUG] Dosya detayları alınamadı (${url}):`, error);
            return {
              id: url,
              originalName: url.split('/').pop() || url,
              displayName: url.split('/').pop() || url,
              uploadDate: productData.updatedAt || new Date(),
              url: url,
              type: url.split('.').pop()?.toLowerCase() || 'unknown',
              size: 0
            };
          }
        });
        
        const documents = await Promise.all(documentPromises);
        console.log('[DEBUG] İşlenmiş doküman verileri:', documents);
        setModelDocuments(documents);
      } else {
        console.log('[DEBUG] Ürünün model dokümanı yok:', productData?.technicalSpec?.modelDocumentUrls);
        setModelDocuments([]);
      }
    } catch (error) {
      console.error('[DEBUG] Model dokümanları fetch hatası:', error);
      notification.error({
        message: 'Hata',
        description: 'Model dokümanları alınamadı'
      });
    }
  };

  // Product verisi değiştiğinde dokümanları güncelle
  useEffect(() => {
    if (product?.technicalSpec?.modelDocumentUrls) {
      console.log('[DEBUG] Product verisi içinden model dokümanları alınıyor...');
      const documents = product.technicalSpec.modelDocumentUrls.map((url: string) => ({
        id: url,
        originalName: url.split('/').pop() || url,
        displayName: url.split('/').pop() || url,
        uploadDate: new Date(product.updatedAt || Date.now()),
        url: url,
        type: url.split('.').pop()?.toLowerCase() || 'unknown',
        size: 0
      } as ModelDocument));
      console.log('[DEBUG] Product verisinden alınan dokümanlar:', documents);
      setModelDocuments(documents);
    } else {
      console.log('[DEBUG] Product verisinde model dokümanı yok');
      setModelDocuments([]);
    }
  }, [product?.technicalSpec?.modelDocumentUrls]);

  // Model dokümanı silme fonksiyonunu güncelle
  const handleDeleteModelDocument = async (documentUrl: string) => {
    try {
      console.log('[DEBUG] Doküman silme isteği:', documentUrl);
      
      // Önce dokümanı bul
      const getDocResponse = await fetch(`${API_URL}/model-documents/product/${id}`);
      if (!getDocResponse.ok) {
        throw new Error('Doküman bilgileri alınamadı');
      }
      
      const docData = await getDocResponse.json();
      const document = docData.documents.find((doc: any) => doc.path === documentUrl);
      
      if (!document) {
        throw new Error('Doküman bulunamadı');
      }

      // Dokümanı sil
      const deleteResponse = await fetch(`${API_URL}/model-documents/${document._id}`, {
        method: 'DELETE'
      });

      if (!deleteResponse.ok) {
        throw new Error('Dosya sunucudan silinemedi');
      }

      // Sonra ürün verisini güncelle
      const updatedTechnicalSpec = {
        ...technicalSpec,
        modelDocumentUrls: technicalSpec.modelDocumentUrls?.filter((url: string) => url !== documentUrl) || []
      };

      const updateResponse = await fetch(`${API_URL}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ technicalSpec: updatedTechnicalSpec })
      });

      if (!updateResponse.ok) {
        throw new Error('Ürün güncellenemedi');
      }

      // State'i güncelle
      setTechnicalSpec(updatedTechnicalSpec);
      setModelDocuments(prev => prev.filter(doc => doc.url !== documentUrl));
      
      notification.success({
        message: 'Başarılı',
        description: 'Doküman başarıyla silindi'
      });
    } catch (error) {
      console.error('[DEBUG] Doküman silme hatası:', error);
      notification.error({
        message: 'Hata',
        description: error instanceof Error ? error.message : 'Doküman silinirken bir hata oluştu'
      });
    } finally {
      setDocumentToDelete(null); // Modal'ı kapat
    }
  };

  // --- Event Handlers ---

const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | { target: { name: string; value: any } }) => {
  const { name, value } = 'target' in e ? e.target : e;
  setFormData(prev => ({
    ...prev,
    [name]: value
  }));
};

  const handleGenericFileDropOrSelectEdit = useCallback((
    acceptedFiles: File[],
    existingUrls: string[],
    currentNewFiles: File[], 
    setNewFiles: React.Dispatch<React.SetStateAction<File[]>>,
    setNewPreviewUrls: React.Dispatch<React.SetStateAction<string[]>>,
    maxFiles: number,
    fileTypeLabel: string
  ) => {
    const currentTotalCount = existingUrls.length + currentNewFiles.length;
    let availableSlots = maxFiles - currentTotalCount;

    if (acceptedFiles.length === 0) {
        return;
    }

    if (acceptedFiles.length > availableSlots) {
      alert(`En fazla ${maxFiles} ${fileTypeLabel} olabilir. Sadece ${availableSlots} yeni dosya eklenecek.`);
      acceptedFiles = acceptedFiles.slice(0, availableSlots);
    }

    // Dosya boyutu ve tipi kontrolü
    const validFiles = acceptedFiles.filter(file => {
      // ... (validation logic remains the same) ...
      return true; // Assuming validation logic is correct for now
    });

    if (validFiles.length > 0) {
      // Dosya state'ini doğrudan güncelle
      const updatedFiles = [...currentNewFiles, ...validFiles];
      setNewFiles(updatedFiles);

      // Sadece yeni eklenen geçerli dosyalar için önizleme URL'leri oluştur
      const newPreviews = validFiles.map(file =>
        file.type === 'application/pdf' ? '/pdf-icon.png' : URL.createObjectURL(file)
      );

      // Önizleme URL'leri state'ini fonksiyonel güncelleme ile güncelle
      setNewPreviewUrls(prevUrls => {
        const combinedUrls = [...prevUrls, ...newPreviews];
        return combinedUrls;
      });
    }
  }, []); // Bu fonksiyonun state bağımlılığı yok, sadece setter ve argüman kullanıyor.

  // --- Ana Görsel Handler'ları ---
  const handleFileDropOrSelect = useCallback((acceptedFiles: File[]) => {
    handleGenericFileDropOrSelectEdit(acceptedFiles, existingImageUrls, newlySelectedFiles, setNewlySelectedFiles, setNewImagePreviewUrls, 20, "görsel");
  }, [existingImageUrls, newlySelectedFiles, handleGenericFileDropOrSelectEdit]); // Bağımlılıkları kontrol edin: existingImageUrls, newlySelectedFiles, handleGenericFileDropOrSelectEdit

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileDropOrSelect(Array.from(e.target.files));
    }
    e.target.value = ''; // Input'u sıfırla
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => { /* ... (CreateProduct ile aynı) ... */
    e.preventDefault(); e.stopPropagation(); setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    e.dataTransfer.dropEffect = 'copy'; 
    if (!isDragging) setIsDragging(true);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsDragging(false);
    handleFileDropOrSelect(Array.from(e.dataTransfer.files));
  };

  // Yeni yüklenen bir görseli kaldırma
  const removeNewImage = (index: number) => {
    setNewlySelectedFiles(prevFiles => {
        const updated = prevFiles.filter((_, i) => i !== index);
        return updated;
    });
    setNewImagePreviewUrls(prevUrls => {
        const urlToRemove = prevUrls[index];
        if (urlToRemove && urlToRemove !== '/pdf-icon.png') {
            URL.revokeObjectURL(urlToRemove); // Belleği serbest bırak
        }
        const updated = prevUrls.filter((_, i) => i !== index);
        return updated;
    });
  };

  // Mevcut (daha önce kaydedilmiş) bir görseli kaldırma
  const removeExistingImage = (index: number) => {
    const updatedUrls = [...existingImageUrls];
    const removedUrl = updatedUrls[index];
    updatedUrls.splice(index, 1);
    setExistingImageUrls(updatedUrls);
  };

  // --- Teknik Çizim Handler'ları ---
  const handleTechDrawFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // newTechnicalDrawingFiles argümanını tekrar ekleyin
      handleGenericFileDropOrSelectEdit(Array.from(e.target.files), existingTechnicalDrawingUrls, newTechnicalDrawingFiles, setNewTechnicalDrawingFiles, setNewTechnicalDrawingPreviewUrls, 5, "teknik çizim");
    }
    e.target.value = '';
  };
  const handleTechDrawDragEnter = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsTechDrawingDragging(true); 
  };
  const handleTechDrawDragLeave = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsTechDrawingDragging(false); 
  };
  const handleTechDrawDragOver = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    e.dataTransfer.dropEffect = 'copy'; 
    if (!isTechDrawingDragging) setIsTechDrawingDragging(true); 
  };
  const handleTechDrawDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsTechDrawingDragging(false);
    // newTechnicalDrawingFiles argümanını tekrar ekleyin
    handleGenericFileDropOrSelectEdit(Array.from(e.dataTransfer.files), existingTechnicalDrawingUrls, newTechnicalDrawingFiles, setNewTechnicalDrawingFiles, setNewTechnicalDrawingPreviewUrls, 5, "teknik çizim");
  };
  const removeNewTechnicalDrawingImage = (index: number) => {
    const url = newTechnicalDrawingPreviewUrls[index];
    if (url !== '/pdf-icon.png') URL.revokeObjectURL(url);
    setNewTechnicalDrawingFiles(prev => prev.filter((_, i) => i !== index));
    setNewTechnicalDrawingPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };
  const removeExistingTechnicalDrawingImage = (index: number) => {
    setExistingTechnicalDrawingUrls(prev => prev.filter((_, i) => i !== index));
  };

  // --- Ölçüm Şekli Handler'ları ---
  const handleMeasureGuideFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // newMeasurementGuideFiles argümanını tekrar ekleyin
      handleGenericFileDropOrSelectEdit(Array.from(e.target.files), existingMeasurementGuideUrls, newMeasurementGuideFiles, setNewMeasurementGuideFiles, setNewMeasurementGuidePreviewUrls, 5, "ölçüm şekli");
    }
    e.target.value = '';
  };
  const handleMeasureGuideDragEnter = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsMeasurementGuideDragging(true); 
  };
  const handleMeasureGuideDragLeave = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsMeasurementGuideDragging(false); 
  };
  const handleMeasureGuideDragOver = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    e.dataTransfer.dropEffect = 'copy'; 
    if (!isMeasurementGuideDragging) setIsMeasurementGuideDragging(true); 
  };
  const handleMeasureGuideDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsMeasurementGuideDragging(false);
    // newMeasurementGuideFiles argümanını tekrar ekleyin
    handleGenericFileDropOrSelectEdit(Array.from(e.dataTransfer.files), existingMeasurementGuideUrls, newMeasurementGuideFiles, setNewMeasurementGuideFiles, setNewMeasurementGuidePreviewUrls, 5, "ölçüm şekli");
  };
  const removeNewMeasurementGuideImage = (index: number) => {
    const url = newMeasurementGuidePreviewUrls[index];
    if (url !== '/pdf-icon.png') URL.revokeObjectURL(url);
    setNewMeasurementGuideFiles(prev => prev.filter((_, i) => i !== index));
    setNewMeasurementGuidePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };
  const removeExistingMeasurementGuideImage = (index: number) => {
    setExistingMeasurementGuideUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Handler for adding/updating a single accessory (called by AccessorySelector via onAccessorySelect)
  const handleAccessorySelect = (accessoryFromSelector: Accessory) => {
    const newAccessory: LocalAccessory = {
      ...accessoryFromSelector,
      quantity: Number(accessoryFromSelector.quantity) || 0, // Convert string quantity to number
    };

    setAccessories(prevAccessories => {
      const existingIndex = prevAccessories.findIndex(acc => acc._id === newAccessory._id);
      if (existingIndex > -1) {
        // Update existing accessory
        const updatedList = [...prevAccessories];
        updatedList[existingIndex] = newAccessory;
        return updatedList;
      } else {
        // Add new accessory
        const updatedList = [...prevAccessories, newAccessory];
        return updatedList;
      }
    });
  };

  // Handler for removing a single accessory (called by AccessorySelector via onAccessoryRemove)
  const handleAccessoryRemove = (accessoryIdToRemove: string) => {
    setAccessories(prevAccessories => {
      const updatedList = prevAccessories.filter(acc => acc._id !== accessoryIdToRemove);
      return updatedList;
    });
  };

  const handleColorSelect = (colorFromSelector: SelectorColorType | null) => {
    console.log('[DEBUG] Seçilen renk:', colorFromSelector);
    
    if (colorFromSelector && colorFromSelector._id) {
      setSelectedColor(colorFromSelector);
      setFormData(prev => ({
        ...prev,
        color: colorFromSelector._id
      }));
    } else {
      setSelectedColor(null);
      setFormData(prev => ({
        ...prev,
        color: ''
      }));
    }
  };

  const handleFabricSelect = (fabric: Fabric | null) => {
    if (fabric) {
      setSelectedFabric(fabric);
      setFormData(prev => ({ ...prev, fabric: fabric._id }));
    } else {
      setSelectedFabric(null);
      setFormData(prev => ({ ...prev, fabric: '' }));
    }
  };

  // Model doküman işleme fonksiyonları
  const handleModelDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleModelDocFilesSelected(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  const handleModelDocDragEnter = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault();
    e.stopPropagation();
    setIsModelDocDragging(true);
  };

  const handleModelDocDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsModelDocDragging(false);
    }
  };

  const handleModelDocDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!isModelDocDragging) setIsModelDocDragging(true);
  };

  const handleModelDocDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModelDocDragging(false);
    handleModelDocFilesSelected(Array.from(e.dataTransfer.files));
  };

  // Model doküman seçimi
  const handleModelDocFilesSelected = (files: File[]) => {
    const maxFiles = 20;
    const remainingSlots = maxFiles - existingModelDocumentUrls.length - newModelDocumentFiles.length;
    
    if (remainingSlots <= 0) {
      notification.warning({
        message: 'Maksimum dosya limitine ulaşıldı',
        description: `En fazla ${maxFiles} model dökümanı yükleyebilirsiniz.`,
      });
      return;
    }

    // Maksimum dosya sayısı kontrolü
    const newFiles = files.slice(0, remainingSlots);
    
    // Dosya tiplerine göre önizleme oluştur
    const newPreviewUrls = newFiles.map(file => {
      if (file.type.includes('pdf')) {
        return '/pdf-icon.png'; // PDF için yer tutucu
      } else if (file.type.includes('spreadsheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        return '/excel-icon.png'; // Excel için yer tutucu
      } else if (file.type.includes('document') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) {
        return '/word-icon.png'; // Word için yer tutucu
      } else if (file.type.includes('zip') || file.name.endsWith('.zip') || file.name.endsWith('.rar')) {
        return '/zip-icon.png'; // Arşiv için yer tutucu
      } else {
        return '/file-icon.png'; // Varsayılan dosya ikonu
      }
    });

    setNewModelDocumentFiles([...newModelDocumentFiles, ...newFiles]);
    setNewModelDocumentPreviewUrls([...newModelDocumentPreviewUrls, ...newPreviewUrls]);
  };

  // Yeni model dokümanı silme
  const removeNewModelDocument = (index: number) => {
    const updatedFiles = [...newModelDocumentFiles];
    const updatedUrls = [...newModelDocumentPreviewUrls];
    
    updatedFiles.splice(index, 1);
    updatedUrls.splice(index, 1);
    
    setNewModelDocumentFiles(updatedFiles);
    setNewModelDocumentPreviewUrls(updatedUrls);
  };

  // Mevcut model dokümanı silme
  const removeExistingModelDocument = (index: number) => {
    const docToDelete = modelDocuments[index].url;
    setDocumentToDelete(docToDelete); // Modal'ı aç
  };

  // --- Save Logic ---
  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      // 1. FormData oluştur
      const formDataToSend = new FormData();
      // Temel alanlar
      Object.entries(formData).forEach(([key, value]) => {
        // Boolean ve array değerleri stringe çevir
        if (typeof value === 'boolean') {
          formDataToSend.append(key, value ? 'true' : 'false');
        } else if (Array.isArray(value)) {
          formDataToSend.append(key, JSON.stringify(value));
        } else {
          formDataToSend.append(key, value ?? '');
        }
      });
      // Aksesuarlar
      formDataToSend.append('accessories', JSON.stringify(accessories));
      // Teknik föy
      formDataToSend.append('technicalSpec', JSON.stringify(technicalSpec));
      // Ana görseller (yeni yüklenenler)
      newlySelectedFiles.forEach(file => formDataToSend.append('images', file));
      // Teknik çizimler
      newTechnicalDrawingFiles.forEach(file => formDataToSend.append('technicalDrawings', file));
      // Ölçüm şekli görselleri
      newMeasurementGuideFiles.forEach(file => formDataToSend.append('measurementGuides', file));
      // Model dokümanları
      newModelDocumentFiles.forEach(file => formDataToSend.append('modelDocuments', file));
      // Mevcut görsel ve doküman yollarını da ekle (backend birleştirsin)
      existingImageUrls.forEach(url => formDataToSend.append('existingImageUrls', url));
      existingTechnicalDrawingUrls.forEach(url => formDataToSend.append('existingTechnicalDrawingUrls', url));
      existingMeasurementGuideUrls.forEach(url => formDataToSend.append('existingMeasurementGuideUrls', url));
      existingModelDocumentUrls.forEach(url => formDataToSend.append('existingModelDocumentUrls', url));
      // Debug
      console.debug('[DEBUG] FormData ile gönderilecek alanlar:', Array.from(formDataToSend.entries()));
      // 2. API isteği
      const response = await fetch(`${API_URL}/products/${id}`, {
        method: 'PUT',
        body: formDataToSend
      });
      if (!response.ok) {
        throw new Error('Ürün güncellenirken bir hata oluştu');
      }
      notification.success({
        message: 'Başarılı',
        description: 'Ürün başarıyla güncellendi',
        placement: 'topRight'
      });
      navigate('/products');
    } catch (error) {
      console.error('Ürün güncelleme hatası:', error);
      notification.error({
        message: 'Hata',
        description: 'Ürün güncellenirken bir hata oluştu.',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  // handleSubmit now just calls the save logic (can be triggered by form or modal)
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault(); // Prevent default form submission if event exists
    handleSaveChanges();
  };

  // --- Technical Spec and Image Handlers ---
  // These handlers interact with the technicalSpec state or image states

  // Size Spec Handlers (assuming managed within TechnicalSpecSheet or passed down)
  const handleAddSizeSpec = () => { /* Logic to add to techSpec.sizeSpecs */ };
  const handleRemoveSizeSpec = (index: number) => { /* Logic to remove from techSpec.sizeSpecs */ };
  const handleSizeSpecChange = (index: number, field: string, value: string | number) => { /* Logic to update techSpec.sizeSpecs[index] */ };
  const handleAddCustomSizeField = () => { /* Logic to update customSizeFields and techSpec.sizeSpecs */ };
  const handleRemoveCustomSizeField = (fieldKey: string) => { setDeleteFieldConfirmation(fieldKey); };

  // Fabric Spec Handlers
  const handleAddFabricSpec = () => { /* Logic to add to techSpec.fabricSpecs */ };
  const handleRemoveFabricSpec = (index: number) => { /* Logic to remove from techSpec.fabricSpecs */ };
  const handleFabricSpecChange = (index: number, field: string, value: string) => { /* Logic to update techSpec.fabricSpecs[index] */ };

  // Stitch Detail Handlers
  const handleAddStitchDetail = () => { /* Logic to add to techSpec.stitchDetails */ };
  const handleRemoveStitchDetail = (index: number) => { /* Logic to remove from techSpec.stitchDetails */ };
  const handleStitchDetailChange = (index: number, field: string, value: string | number) => { /* Logic to update techSpec.stitchDetails[index] */ };

  // Printing Detail Handlers
  const handleAddPrintingDetail = () => { /* Logic to add to techSpec.printingDetails */ };
  const handleRemovePrintingDetail = (index: number) => { /* Logic to remove from techSpec.printingDetails */ };
  const handlePrintingDetailChange = (index: number, field: string, value: string) => { /* Logic to update techSpec.printingDetails[index] */ };

  // Other Tech Spec Handlers
  const handleCareInstructionChange = (value: string) => { setTechnicalSpec((prev: any) => ({ ...prev, careInstructions: value.split('\n').filter(Boolean) })); };
  const handleQualityStandardsChange = (value: string) => { setTechnicalSpec((prev: any) => ({ ...prev, qualityStandards: value.split('\n').filter(Boolean) })); };
  const handleNotesChange = (value: string) => { setTechnicalSpec((prev: any) => ({ ...prev, notes: value })); };

  // TechnicalSpec state'ini güncelle
  const handleDeleteFieldConfirmation = (fieldKey: string) => {
    if (fieldKey) {
      setCustomSizeFields((prev: any) => prev.filter((f: any) => f.key !== fieldKey));
      setTechnicalSpec((prev: any) => ({
        ...prev,
        dynamicSizeTable: {
          ...prev.dynamicSizeTable,
          data: Object.fromEntries(
            Object.entries(prev.dynamicSizeTable?.data || {})
              .filter(([key]) => key !== fieldKey)
          )
        }
      }));
      setDeleteFieldConfirmation(null);
    }
  };

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" tip="Ürün bilgileri yükleniyor..." />
       </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-red-800">{error}</h3>
        <p className="mt-1 text-sm text-gray-500">Tekrar denemek için lütfen sayfayı yenileyin.</p>
        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Geri Dön
          </button>
        </div>
       </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Ürün Bulunamadı</h3>
        <p className="mt-1 text-sm text-gray-500">Bu ürün bulunamadı veya erişim yetkiniz yok.</p>
        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Geri Dön
          </button>
        </div>
       </div>
    );
  }

  // Ensure formData is used safely, e.g., formData.modelName || ''
  const totalImageCount = existingImageUrls.length + newlySelectedFiles.length;
  const totalTechDrawCount = existingTechnicalDrawingUrls.length + newTechnicalDrawingFiles.length;
  const totalMeasureGuideCount = existingMeasurementGuideUrls.length + newMeasurementGuideFiles.length;

  // Dosya boyutu formatlamak için yardımcı fonksiyon
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Yardımcı fonksiyonlar
  const fetchColor = async (colorId: string) => {
    try {
      const response = await fetch(`${API_URL}/colors/${colorId}`);
      if (!response.ok) {
        throw new Error('Renk bilgisi alınamadı');
      }
      const data = await response.json();
      setSelectedColor(data);
    } catch (error) {
      console.error('Renk bilgisi yüklenirken hata:', error);
      notification.error({
        message: 'Hata',
        description: 'Renk bilgisi yüklenemedi'
      });
    }
  };

  const fetchFabric = async (fabricId: string) => {
    try {
      const response = await fetch(`${API_URL}/fabrics/${fabricId}`);
      if (!response.ok) {
        throw new Error('Kumaş bilgisi alınamadı');
      }
      const data = await response.json();
      setSelectedFabric(data);
    } catch (error) {
      console.error('Kumaş bilgisi yüklenirken hata:', error);
      notification.error({
        message: 'Hata',
        description: 'Kumaş bilgisi yüklenemedi'
      });
    }
  };

  const convertToLocalAccessories = (accessories: Accessory[]): LocalAccessory[] => {
    return accessories.map(acc => ({
      ...acc,
      quantity: typeof acc.quantity === 'string' ? parseFloat(acc.quantity) : acc.quantity
    }));
  };

  // Model dokümanı interface'i
  interface ModelDocument {
    id: string;
    originalName: string;
    displayName: string;
    uploadDate: Date;
    url: string;
    type: string;
    size: number;
  }

  // Doküman metadata interface'i
  interface DocumentMetadata {
    path: string;
    [key: string]: any;
  }

  // Yardımcı fonksiyonlar
  const convertToModelDocument = (doc: DocumentMetadata, productUpdatedAt?: string): ModelDocument => ({
    id: doc.path,
    originalName: doc.path.split('/').pop() || doc.path,
    displayName: doc.path.split('/').pop() || doc.path,
    uploadDate: new Date(productUpdatedAt || Date.now()),
    url: doc.path,
    type: doc.path.split('.').pop()?.toLowerCase() || 'unknown',
    size: 0
  });

  const handleImageUrlsMap = (urls: string[]): string[] => {
    return urls.map((url: string) => url.startsWith('/') ? url : `/${url}`);
  };

  // --- Render Logic ---
  return (
    <div className="p-4 md:p-8 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          {/* Use formData with fallback to product, or empty string */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2 sm:mb-0">Ürün Düzenle: {formData.modelName || product.modelName || ''}</h1>
          <Space wrap>
            <AntButton onClick={() => setCancelModalOpen(true)} disabled={loading}>İptal</AntButton>
            <AntButton type="primary" onClick={() => setConfirmModalOpen(true)} loading={loading}>
              {loading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </AntButton>
          </Space>
        </div>

        {/* Form Area with Tabs */}
        <form onSubmit={handleSubmit}>
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="Temel Bilgiler" key="1">
              <div className="space-y-8 mt-4">
                {/* Temel Bilgiler Card */}
                <Card>
                  <SectionTitle>Temel Bilgiler</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                    {/* Model Adı */}
                    <div>
                      <label className="form-label">Model Adı <span className="text-red-500">*</span></label>
                      <Input name="modelName" value={formData.modelName || ''} onChange={handleChange} required />
                    </div>
                    {/* Satın Alma Grubu */}
                    <div>
                      <label className="form-label">Satın Alma Grubu <span className="text-red-500">*</span></label>
                      <Select
                        value={formData.purchasingGroup || ''}
                        onChange={(value) => handleChange({ target: { name: 'purchasingGroup', value } })}
                        className="w-full"
                      >
                        <Select.Option value="knitting">Örme</Select.Option>
                        <Select.Option value="weaving">Dokuma</Select.Option>
                        <Select.Option value="triko">Triko</Select.Option>
                        <Select.Option value="denim">Denim</Select.Option>
                      </Select>
                    </div>
                    {/* Model Stili */}
                    <div>
                      <label className="form-label">Model Stili</label>
                      <Select
                        value={formData.modelStyle || ''}
                        onChange={(value) => handleChange({ target: { name: 'modelStyle', value } })}
                        className="w-full"
                        allowClear
                      >
                        <Select.Option value="">Seçiniz...</Select.Option>
                        <Select.Option value="Baskı">Baskı</Select.Option>
                        <Select.Option value="Düz">Düz</Select.Option>
                        <Select.Option value="Nakışlı">Nakışlı</Select.Option>
                        <Select.Option value="Taşlı">Taşlı</Select.Option>
                      </Select>
                    </div>
                    {/* Kumaş */}
                    <div>
                      <label className="form-label">Kumaş <span className="text-red-500">*</span></label>
                      <FabricSelector selectedFabric={selectedFabric} onFabricSelect={handleFabricSelect} />
                    </div>
                    {/* Renk */}
                    <div>
                      <label className="form-label mb-2">Renk <span className="text-red-500">*</span></label>
                      <ColorSelector selectedColor={typeof product?.color === 'object' ? product.color : null} onColorSelect={handleColorSelect} />
                    </div>
                    {/* Ürün Tipi */}
                    <div>
                      {hasPermission("orders", "update") || hasPermission("orders", "create") ? (
                        <>
                          <label className="form-label">Ürün Tipi</label>
                          <Select
                            value={formData.isPrototype ? 'true' : 'false'}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                isPrototype: value === 'true',
                              }))
                            }
                            className="w-full"
                          >
                            <Select.Option value="true">Prototip</Select.Option>
                            <Select.Option value="false">Normal Ürün</Select.Option>
                          </Select>
                        </>
                      ) : ""}
                      
                      {formData.isPrototype && formData.prototypeCode && (
                        <p className="text-xs text-gray-500 mt-1">Prototip Kodu: {formData.prototypeCode}</p>
                      )}
                      {!formData.isPrototype && formData.productCode && (
                        <p className="text-xs text-gray-500 mt-1">Ürün Kodu: {formData.productCode}</p>
                      )}
                    </div>
                    {/* Açıklama */}
                    <div className="md:col-span-2">
                      <label className="form-label">Açıklama</label>
                      <Input.TextArea name="description" value={formData.description || ''} onChange={handleChange} rows={3} placeholder="Ürün hakkında detaylı açıklama..." />
                    </div>
                    {/* Aksesuarlar */}
                    <div className="md:col-span-2">
                      <label className="form-label">Aksesuarlar</label>
                      <AccessorySelector
                        selectedAccessories={accessories}
                        onAccessorySelect={handleAccessorySelect}
                        onAccessoryRemove={handleAccessoryRemove}
                      />
                    </div>
                  </div>
                </Card>

                <Divider />

                {/* Ana Görseller Upload */}
                <FileUploadAreaEdit
                  title="Ana Ürün Görselleri"
                  existingUrls={product?.imageUrls || []}
                  newFiles={newlySelectedFiles}
                  newPreviewUrls={newImagePreviewUrls}
                  onFilesSelected={handleFileDropOrSelect}
                  onRemoveExisting={removeExistingImage}
                  onRemoveNew={removeNewImage}
                  maxFiles={20}
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  fileTypeLabel="ana görsel"
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Teknik Föy" key="2">
              <div className="space-y-8 mt-4">
                {/* Teknik Föy */}
                <Card>
                  <SectionTitle>Teknik Föy</SectionTitle>
                  <TechnicalSpecSheet
                    techSpec={product?.technicalSpec || {
                      sizeSpecs: [],
                      measurementFields: [],
                      sizeSpecFields: [],
                      stitchDetails: [],
                      fabricSpecs: [],
                      printingDetails: [],
                      careInstructions: [],
                      qualityStandards: [],
                      notes: '',
                      dynamicSizeTable: { rows: [], columns: [], data: {} }
                    }}
                    setTechSpec={setTechnicalSpec}
                  />
                </Card>

                <Divider />

                {/* Teknik Çizimler Upload */}
                <FileUploadAreaEdit
                  title="Teknik Çizimler"
                  existingUrls={existingTechnicalDrawingUrls}
                  newFiles={newTechnicalDrawingFiles}
                  newPreviewUrls={newTechnicalDrawingPreviewUrls}
                  onFilesSelected={(files) => handleGenericFileDropOrSelectEdit(files, existingTechnicalDrawingUrls, newTechnicalDrawingFiles, setNewTechnicalDrawingFiles, setNewTechnicalDrawingPreviewUrls, 5, "teknik çizim")}
                  onRemoveExisting={removeExistingTechnicalDrawingImage}
                  onRemoveNew={removeNewTechnicalDrawingImage}
                  maxFiles={5}
                  accept="image/jpeg, image/png, image/gif, image/webp, application/pdf"
                  fileTypeLabel="teknik çizim"
                />

                <Divider />

                {/* Ölçüm Şekli Görselleri Upload */}
                <FileUploadAreaEdit
                  title="Ölçüm Şekli Görselleri"
                  existingUrls={existingMeasurementGuideUrls}
                  newFiles={newMeasurementGuideFiles}
                  newPreviewUrls={newMeasurementGuidePreviewUrls}
                  onFilesSelected={(files) => handleGenericFileDropOrSelectEdit(files, existingMeasurementGuideUrls, newMeasurementGuideFiles, setNewMeasurementGuideFiles, setNewMeasurementGuidePreviewUrls, 5, "ölçüm şekli")}
                  onRemoveExisting={removeExistingMeasurementGuideImage}
                  onRemoveNew={removeNewMeasurementGuideImage}
                  maxFiles={5}
                  accept="image/jpeg, image/png, image/gif, image/webp, application/pdf"
                  fileTypeLabel="ölçüm şekli"
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Model Dokümanları" key="3">
              <div className="space-y-8 mt-4">
                <Card>
                  <SectionTitle>Model Dokümanları</SectionTitle>
                  {/* Debug bilgisi */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="mb-4 p-4 bg-gray-100 rounded text-xs">
                      <p>Debug Bilgisi:</p>
                      <p>Toplam Doküman: {modelDocuments.length}</p>
                      <p>Product ID: {id}</p>
                      <p>Product Yüklendi: {product ? 'Evet' : 'Hayır'}</p>
                      <pre className="mt-2 overflow-auto">
                        {JSON.stringify(modelDocuments, null, 2)}
                      </pre>
                    </div>
                  )}
                  <div className="pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mevcut Dokümanlar: {modelDocuments.length}, Yeni: {newModelDocumentFiles.length} (Maks: 20)
                    </label>
                    {/* Yükleme Alanı */}
                    <div
                      onDragEnter={handleModelDocDragEnter}
                      onDragLeave={handleModelDocDragLeave}
                      onDragOver={handleModelDocDragOver}
                      onDrop={handleModelDocDrop}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ease-in-out mb-6
                        ${isModelDocDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}
                        ${(modelDocuments.length + newModelDocumentFiles.length) >= 20 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      onClick={() => {
                        const inputEl = document.getElementById('model-doc-upload') as HTMLInputElement;
                        if (inputEl && (modelDocuments.length + newModelDocumentFiles.length) < 20) {
                          inputEl.click();
                        }
                      }}
                    >
                      <input
                        id="model-doc-upload"
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,image/*"
                        multiple
                        onChange={handleModelDocFileChange}
                        className="hidden"
                        disabled={(modelDocuments.length + newModelDocumentFiles.length) >= 20}
                      />
                      <div className="flex flex-col items-center">
                        <FilePdfOutlined className={`text-4xl mb-3 ${isModelDocDragging ? 'text-indigo-600' : 'text-gray-400'}`} />
                        <p className={`text-sm font-medium ${isModelDocDragging ? 'text-indigo-700' : 'text-gray-600'}`}>
                          Model dokümanlarını buraya sürükleyin veya seçin
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Desteklenen tipler: PDF, DOC, XLS, PPT, TXT, ZIP, RAR, resimler (Maks 50MB)
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Toplam Yüklenen: {modelDocuments.length + newModelDocumentFiles.length} / 20
                        </p>
                      </div>
                    </div>

                    {/* Yüklü Dokümanlar Listesi */}
                    <div className="mt-8">
                      <h5 className="text-sm font-medium text-gray-700 mb-4">Yüklü Dokümanlar</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {/* Mevcut Dokümanlar */}
                        {modelDocuments.map((doc) => {
                          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.url);
                          const isPdf = /\.pdf$/i.test(doc.url);
                          const isExcel = /\.(xlsx|xls)$/i.test(doc.url);
                          const isWord = /\.(doc|docx)$/i.test(doc.url);
                          const isZip = /\.(zip|rar)$/i.test(doc.url);

                          return (
                            <div key={doc.id} className="border rounded-lg p-4 flex flex-col relative group">
                              {/* Dosya Önizleme/İkon Alanı */}
                              <div className="mb-3 h-32 flex items-center justify-center bg-gray-50 rounded">
                                {isImage ? (
                                  <img
                                    src={`${API_URL}${doc.url}`}
                                    alt={doc.displayName}
                                    className="h-full w-full object-contain rounded"
                                  />
                                ) : (
                                  <div className="text-4xl text-gray-400">
                                    {isPdf && <FilePdfOutlined className="text-red-500" />}
                                    {isExcel && <FileExcelOutlined className="text-green-500" />}
                                    {isWord && <FileWordOutlined className="text-blue-500" />}
                                    {isZip && <FileZipOutlined className="text-yellow-500" />}
                                    {!isPdf && !isExcel && !isWord && !isZip && <FileOutlined />}
                                  </div>
                                )}
                              </div>

                              <div className="text-sm font-medium mb-2 truncate" title={doc.displayName}>
                                {doc.displayName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(doc.uploadDate).toLocaleDateString('tr-TR')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatFileSize(doc.size)}
                              </div>
                              <div className="mt-2 flex items-center space-x-2">
                                {doc.url && (
                                  <a 
                                    href={`${API_URL}${doc.url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                  >
                                    <EyeOutlined className="mr-1" /> Görüntüle
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setDocumentToDelete(doc.url)}
                                  className="text-red-600 hover:text-red-800 text-sm flex items-center"
                                >
                                  <DeleteOutlined className="mr-1" /> Sil
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        {/* Yeni Yüklenen Dokümanlar */}
                        {newModelDocumentFiles.map((file, index) => {
                          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
                          const isPdf = /\.pdf$/i.test(file.name);
                          const isExcel = /\.(xlsx|xls)$/i.test(file.name);
                          const isWord = /\.(doc|docx)$/i.test(file.name);
                          const isZip = /\.(zip|rar)$/i.test(file.name);

                          return (
                            <div key={`new-${index}`} className="border-2 border-blue-200 rounded-lg p-4 flex flex-col relative group bg-blue-50">
                              {/* Dosya Önizleme/İkon Alanı */}
                              <div className="mb-3 h-32 flex items-center justify-center bg-white rounded">
                                {isImage ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="h-full w-full object-contain rounded"
                                    onLoad={() => URL.revokeObjectURL(URL.createObjectURL(file))}
                                  />
                                ) : (
                                  <div className="text-4xl text-gray-400">
                                    {isPdf && <FilePdfOutlined className="text-red-500" />}
                                    {isExcel && <FileExcelOutlined className="text-green-500" />}
                                    {isWord && <FileWordOutlined className="text-blue-500" />}
                                    {isZip && <FileZipOutlined className="text-yellow-500" />}
                                    {!isPdf && !isExcel && !isWord && !isZip && <FileOutlined />}
                                  </div>
                                )}
                              </div>

                              <div className="text-sm font-medium mb-2 truncate" title={file.name}>
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                Yeni Dosya
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </div>
                              <button
                                onClick={() => removeNewModelDocument(index)}
                                className="text-red-600 hover:text-red-800 text-sm flex items-center mt-2"
                              >
                                <DeleteOutlined className="mr-1" /> İptal
                              </button>
                            </div>
                          );
                        })}

                        {modelDocuments.length === 0 && newModelDocumentFiles.length === 0 && (
                          <div className="col-span-full text-center py-8 bg-gray-50 rounded-lg">
                            <p className="text-gray-500">Henüz model dokümanı yüklenmemiş.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </Tabs.TabPane>
          </Tabs>

          {/* Hidden submit button */}
          <button type="submit" className="hidden">Submit</button>
        </form>
      </div>

      {/* Modals */}
       <Modal
         isOpen={confirmModalOpen}
         onClose={() => setConfirmModalOpen(false)}
         onConfirm={handleSubmit}
         title="Değişiklikleri Kaydet"
         type="info"
         confirmText="Kaydet"
         cancelText="İptal"
       >
         <p>Yaptığınız değişiklikleri kaydetmek istediğinize emin misiniz?</p>
       </Modal>

       <Modal
         isOpen={cancelModalOpen}
         onClose={() => setCancelModalOpen(false)}
         onConfirm={() => navigate(-1)} // Go back
         title="Düzenlemeyi İptal Et"
         type="warning"
         confirmText="Evet, İptal Et"
         cancelText="Hayır"
       >
         <p>Yaptığınız değişiklikler kaydedilmeyecek. Devam etmek istiyor musunuz?</p>
       </Modal>

       {/* Delete Field Confirmation Modal */}
       <Modal
         isOpen={!!deleteFieldConfirmation}
         onClose={() => setDeleteFieldConfirmation(null)}
         onConfirm={() => {
           if (deleteFieldConfirmation) {
             // Logic to remove field from customSizeFields and techSpec
             setCustomSizeFields((prev: any) => prev.filter((f: any) => f.key !== deleteFieldConfirmation));
             setTechnicalSpec((prev: any) => ({
                 ...prev,
                 dynamicSizeTable: {
                     ...prev.dynamicSizeTable,
                     data: {
                         ...prev.dynamicSizeTable?.data,
                         [deleteFieldConfirmation]: {}
                     }
                 }
             }));
             setDeleteFieldConfirmation(null);
           }
         }}
         title="Ölçü Alanını Sil"
         type="warning"
         confirmText="Evet, Sil"
         cancelText="İptal"
       >
         <p>{`'${deleteFieldConfirmation}' ölçü alanını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}</p>
       </Modal>

       {/* Doküman Silme Onay Modalı */}
       <Modal
         isOpen={!!documentToDelete}
         onClose={() => setDocumentToDelete(null)}
         onConfirm={() => {
           if (documentToDelete) {
             handleDeleteModelDocument(documentToDelete);
           }
         }}
         title="Dokümanı Sil"
         type="warning"
         confirmText="Evet, Sil"
         cancelText="İptal"
       >
         <p>Bu dokümanı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
         {documentToDelete && (
           <p className="mt-2 text-sm text-gray-500">
             Silinecek doküman: {documentToDelete.split('/').pop()}
           </p>
         )}
       </Modal>

    </div>
  );
};

// Helper styles (optional)
const formLabel = "block text-sm font-medium text-gray-700 mb-1";
const tabButton = "pb-4 px-1 border-b-2 font-medium text-sm";
const tabActive = "border-indigo-500 text-indigo-600";
const tabInactive = "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";
const imageGrid = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3";
const imagePreview = "w-full h-full object-cover rounded border border-gray-300";
const imageOverlay = "absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center p-0.5 rounded-b";
const btnRemoveImage = "absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-600";

// Apply styles using className, e.g., className={formLabel}

export default ProductEdit;